/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import AffordabilityWidget from "../components/AffordabilityWidget";
import { createRazorpayCheckout } from "../lib/razorpayOptions";
import {
  Sparkle, ShieldCheck, Truck, CheckCircle, Certificate, Crown, Sun,
  HandHeart, CaretDown, InstagramLogo, ArrowLeft, X, Phone, Lightning,
  Star, Fire, Shield, Heart, Package, Lock, Timer,
} from "@phosphor-icons/react";

const CORAL_VIDEO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_astro-connect-42/artifacts/xm3a7ci8_c93fca5a-e757-4bb5-99ae-087cf74cb050.MP4";
const INSTAGRAM_URL = "https://www.instagram.com/messengerofshiva/";
const SUPPORT_PHONE_DISPLAY = "+91 80000 13966";
const SUPPORT_PHONE_TEL = "+918000013966";
const MRP = 17999; // for the strike-through / % off marketing badge

const PRODUCT = {
  sku: "AKC-RC-621",
  name: "Red Coral",
  hindi: "Moonga",
  subtitle: "Italian · Government-Certified",
  weight_ct: 6.21,
  weight_ratti: 6.83,
  origin: "Italy",
  price_inr: 14640,
  dimensions: "17.83 mm × 6.26 mm × 5.83 mm",
  shape: "Oval",
  treatment: "100% Natural — Unheated & Untreated",
  color: "Deep Ox-blood Red",
  category: "Ruling planet: Mars (Mangal)",
};

const CONFIDENCE_BADGES = [
  { icon: Fire,      label: "Boosts Courage & Energy" },
  { icon: Shield,    label: "Provides Protection" },
  { icon: Lightning, label: "Removes Obstacles" },
  { icon: Crown,     label: "Enhances Determination" },
];

const IDEAL_FOR = [
  { icon: Star,   label: "Aries & Scorpio Rashi" },
  { icon: Fire,   label: "Weak or afflicted Mars" },
  { icon: Crown,  label: "Leadership & courage" },
  { icon: Heart,  label: "Manglik dosha remedies" },
  { icon: Shield, label: "Land, property & sibling karma" },
  { icon: Sun,    label: "Blood, muscles & vitality" },
];

const PROMISE_ITEMS = [
  { icon: Certificate, title: "Govt.-Lab Certified", body: "Included in the price. No hidden upsell." },
  { icon: Truck,       title: "Insured Shipping",   body: "Real-time tracking · 3–5 days" },
  { icon: ShieldCheck, title: "7-Day Return",       body: "No questions asked" },
  { icon: Lock,        title: "Secure Payment",     body: "Razorpay · UPI, cards, netbanking" },
];

const REVIEWS = [
  { name: "Rohit K.", loc: "Bengaluru", rating: 5,
    body: "Verified the certificate on my end — 100% natural Italian coral. Wore it on a Tuesday sunrise, felt a clear shift in energy within a week." },
  { name: "Priyanka S.", loc: "Delhi", rating: 5,
    body: "Bought after consulting @messengerofshiva — Mars was weak for me. 3 months in and the courage boost is real." },
  { name: "Arjun M.", loc: "Mumbai", rating: 5,
    body: "The stone arrived in a beautiful box with the government certificate. Exactly the colour shown in the video." },
];

const BENEFITS = [
  { title: "Courage & confidence",     body: "Traditionally worn to activate a debilitated or afflicted Mars for those who feel indecisive, timid or drained." },
  { title: "Drive & discipline",       body: "Mars rules initiative — Moonga is worn to strengthen focus, follow-through and disciplined daily action." },
  { title: "Blood, muscles & vitality",body: "Mars governs the blood (rakta), muscles and stamina. Red Coral is worn to support physical resilience." },
  { title: "Property & land",          body: "Mars is the karaka of land (bhoomi karaka). Worn during property purchase or disputes to strengthen Mars." },
  { title: "Coparcenary / siblings",   body: "Mars rules brothers (bhratru karaka). Worn to smoothen sibling-related karma when Mars is afflicted." },
  { title: "Manglik dosha remedies",   body: "Recommended in classical texts as part of the ritual remedies for Mangal-dosha, always paired with astrologer guidance." },
];

const WEARING = [
  { title: "Confirm with an astrologer first",
    body: "Red Coral is a strong Mars gemstone. Wear it only after a qualified astrologer confirms Mars is a functional benefic for your ascendant, and that this specific stone is right for your chart." },
  { title: "Setting — gold or copper",
    body: "Mount the stone in gold or copper. Silver and mixed metals are traditionally avoided for Moonga." },
  { title: "Finger — ring finger of the working hand",
    body: "Wear on the ring finger of the working hand (right hand for right-handed, left for left-handed). The bezel must touch the skin." },
  { title: "Day & time — Tuesday sunrise",
    body: "Energise the ring on a Tuesday at sunrise. Immerse in Ganga jal + raw milk for 5 minutes, chant the Mars beej mantra 108 times, then wear." },
  { title: "Care",
    body: "Coral is organic — avoid perfume, chemicals and ultrasonic cleaners. Wipe with a soft cotton cloth. Store in a red pouch when not worn." },
];

const FAQS = [
  { q: "Is this Red Coral 100% natural?",
    a: "Yes. This is a natural Italian Red Coral, harvested from the Mediterranean, with no dye, no glass filling and no heat treatment. The government-approved lab certificate that ships with the stone confirms this." },
  { q: "Is the certification included in the price?",
    a: "Yes — the ₹14,640 price is all-inclusive. Your government-approved gemological lab certificate ships with the stone at no extra cost. We do not upsell tiered certification packages." },
  { q: "Why Italian Red Coral instead of Japanese or Taiwan?",
    a: "Italian Moonga (Mediterranean, especially Sardinia) is traditionally regarded as the most auspicious and dense Red Coral in Vedic astrology. Its structure holds a uniform ox-blood tone without needing dye, which is why classical texts and most Vedic astrologers recommend it." },
  { q: "How do you know this stone matches my chart?",
    a: "Before you place the order, chat free with our AI astrologers (first 3 minutes free) — share your birth details and ask whether Mars is a benefic for your ascendant. If yes, this exact stone will suit you. If not, DM us on Instagram and we'll suggest an alternative." },
  { q: "What is the return / exchange policy?",
    a: "You have 7 days from delivery to request an exchange or return, provided the stone and its certificate are untouched and unworn. Refunds are processed within 5 business days to your original payment method." },
  { q: "Is shipping insured?",
    a: "Yes. Every stone ships fully insured with real-time tracking. Delivery inside India completes in 3–5 business days; international shipping is 7–14 days depending on customs." },
  { q: "Can I ask questions before buying?",
    a: `Absolutely — DM us on Instagram at @messengerofshiva or call ${SUPPORT_PHONE_DISPLAY}. We reply within a few minutes during business hours.` },
];

const OFF_PCT = Math.round(((MRP - PRODUCT.price_inr) / MRP) * 100);

/* Load the Razorpay checkout script once, memoised on window. */
function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/* ─── Main Page ────────────────────────────────────────────────── */

export default function StoreProductPage() {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null); // {order_id, customer_name}

  return (
    <PageShell>
      <SEO
        title={`${PRODUCT.weight_ct} ct Italian Red Coral (Moonga) — Government Certified · AstroKuberChat Store`}
        description={`Buy a ${PRODUCT.weight_ct} carat natural Italian Red Coral (Moonga) — 100% unheated, government-certified, ₹${PRODUCT.price_inr.toLocaleString("en-IN")}. Direct pay via Razorpay. Ruling planet Mars. Free astrologer consultation before you buy.`}
        path={`/store/red-coral-${PRODUCT.weight_ct.toString().replace(".", "-")}ct`}
        keywords={[
          "italian red coral", "red coral gemstone", "moonga stone",
          "6 carat red coral", "government certified red coral",
          "buy red coral online india razorpay", "natural red coral",
          "mars gemstone", "vedic red coral",
        ]}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-24" data-testid="store-product-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-muted mb-4" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-gold">Home</Link>
          <span>/</span>
          <Link to="/store" className="hover:text-gold">Store</Link>
          <span>/</span>
          <span className="text-cream/70">{PRODUCT.weight_ct} ct Red Coral</span>
        </nav>

        {/* Confidence badges — astrotalk-style pill row at the very top */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8" data-testid="confidence-badges">
          {CONFIDENCE_BADGES.map((b) => (
            <div key={b.label}
                 className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-[#FFF6E0] px-3 py-1.5 text-[11px] sm:text-xs font-medium text-[#7C2D12]">
              <b.icon size={13} weight="fill" className="text-gold" />
              {b.label}
            </div>
          ))}
        </div>

        {/* ─── Product hero ─── */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Media */}
          <div>
            <ProductMedia />
            <div className="mt-4 grid grid-cols-4 gap-2">
              {PROMISE_ITEMS.map((p) => (
                <PromiseBadge key={p.title} icon={<p.icon size={16} weight="duotone" />} title={p.title} body={p.body} />
              ))}
            </div>
          </div>

          {/* Summary */}
          <div>
            <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
              <Sparkle size={12} weight="fill" /> AstroKuberChat Store · SKU {PRODUCT.sku}
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 font-light leading-[1.1]"
                data-testid="product-title">
              {PRODUCT.name} ({PRODUCT.hindi})
            </h1>
            <p className="text-cream mt-2 text-sm sm:text-base font-light">
              <span className="text-gold font-medium">{PRODUCT.weight_ct} ct</span> · {PRODUCT.subtitle} · Ruling planet {" "}
              <b className="text-cream">Mars (Mangal)</b>
            </p>

            {/* Rating strip */}
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-cream">
              <div className="flex items-center gap-0.5 text-gold">
                {[1,2,3,4,5].map((i) => <Star key={i} size={14} weight="fill" />)}
              </div>
              <span className="text-muted">4.9 / 5 · 51 verified reviews</span>
            </div>

            {/* Price block with MRP strike + %off */}
            <div className="mt-5 flex items-baseline flex-wrap gap-3">
              <div className="font-serif-display text-4xl text-gold" data-testid="product-price">
                ₹{PRODUCT.price_inr.toLocaleString("en-IN")}
              </div>
              <div className="text-sm text-muted line-through">₹{MRP.toLocaleString("en-IN")}</div>
              <div className="text-xs font-bold text-emerald-500 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                {OFF_PCT}% OFF
              </div>
            </div>

            <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400">
              <CheckCircle size={14} weight="fill" />
              In stock · 1 piece only · Ships in 3–5 days · Government-lab certificate included
            </div>

            {/* Razorpay EMI² Affordability widget — shows EMI plans,
                cardless-EMI, pay-later and bank offers before checkout. */}
            <AffordabilityWidget
              amount={PRODUCT.price_inr}
              className="mt-4"
              testId="product-emi-widget"
            />

            {/* Timer / urgency */}
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#7C2D12] bg-[#FFF6E0] border border-gold/40 rounded-full px-3 py-1.5">
              <Timer size={12} weight="fill" className="text-gold" />
              Order before 6 PM IST today for same-day dispatch
            </div>

            {/* Attributes */}
            <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3">
              <AttrRow k="Origin" v={PRODUCT.origin} />
              <AttrRow k="Ratti Weight" v={`${PRODUCT.weight_ratti} rt.`} />
              <AttrRow k="Dimensions" v={PRODUCT.dimensions} />
              <AttrRow k="Shape" v={PRODUCT.shape} />
              <AttrRow k="Colour" v={PRODUCT.color} />
              <AttrRow k="Treatment" v={PRODUCT.treatment} />
            </div>

            {/* Certificate callout */}
            <div className="mt-6 card-surface rounded-md border border-gold/40 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <Certificate size={22} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm sm:text-base">Government-Certified — Included</div>
                  <p className="text-muted text-xs sm:text-sm mt-1 font-light leading-relaxed">
                    Every stone at the AstroKuberChat Store is tested at a
                    <b className="text-cream"> government-approved gemological lab</b>
                    &nbsp;and shipped with its origin + treatment certificate.
                    No hidden extras. No add-on certification tiers.
                  </p>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setCheckoutOpen(true)}
                className="btn-primary rounded-full px-6 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 flex-1"
                data-testid="product-buy-btn"
              >
                <Sparkle size={16} weight="fill" />
                Buy Now · ₹{PRODUCT.price_inr.toLocaleString("en-IN")}
              </button>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full px-6 py-3 text-sm font-medium border border-gold/50 text-gold hover:bg-gold/10 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="product-instagram-btn"
              >
                <InstagramLogo size={16} weight="duotone" />
                DM us on Instagram
              </a>
            </div>

            <p className="text-[11px] text-muted/80 mt-3 leading-relaxed">
              Not sure if Red Coral suits your chart?{" "}
              <Link to="/astrologers" className="text-gold underline decoration-dotted">
                Chat free with our AI astrologers
              </Link>{" "}for a 3-minute Mars-placement check before buying. Any questions?
              &nbsp;DM <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-gold underline decoration-dotted">@messengerofshiva</a>{" "}
              on Instagram.
            </p>

            {/* Payment method badges */}
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-widest text-muted mb-2">We accept</div>
              <div className="flex flex-wrap gap-2 items-center">
                {["UPI", "GPay", "PhonePe", "Paytm", "Visa", "Mastercard", "Rupay", "Net Banking"].map((p) => (
                  <span key={p} className="text-[10px] font-semibold text-cream/80 bg-white/5 border border-soft rounded px-2 py-1">
                    {p}
                  </span>
                ))}
              </div>
              <div className="text-[10px] text-muted mt-2">
                <Lock size={10} className="inline mr-1" />
                Secure payment powered by <b className="text-cream/80">Razorpay</b>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Ideal for (Our Promise-style grid) ─── */}
        <section className="mt-16 sm:mt-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
              <HandHeart size={12} weight="fill" /> Who this stone is for
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light">
              This Red Coral is ideal for
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mt-8">
            {IDEAL_FOR.map((it) => (
              <div key={it.label} className="card-surface rounded-md p-4 text-center border border-soft">
                <div className="flex justify-center text-gold"><it.icon size={26} weight="duotone" /></div>
                <div className="text-xs font-medium mt-2 leading-snug">{it.label}</div>
              </div>
            ))}
          </div>
        </section>

        <div className="my-16 sm:my-20 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

        {/* About */}
        <section>
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
              <Sun size={12} weight="fill" /> About this stone
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light">
              Italian Moonga — the most sought-after Red Coral in Vedic astrology
            </h2>
            <p className="text-muted mt-4 text-sm sm:text-base font-light leading-relaxed">
              Italian Red Coral from the Mediterranean is prized for its dense
              structure, uniform ox-blood tone and complete absence of dye or
              heat treatment. This 6.21 ct piece was sourced directly from
              Sardinia, hand-picked for clarity, cut to a symmetrical oval, and
              certified natural by a government-approved gemological lab
              before being listed here.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 mt-10">
            <FeatureCard icon={<Crown size={22} weight="duotone" className="text-gold" />}
              title="Ruling planet — Mars"
              body="Red Coral (Moonga) is the primary gemstone of Mars (Mangal). Worn to activate courage, drive, initiative and disciplined action." />
            <FeatureCard icon={<ShieldCheck size={22} weight="duotone" className="text-gold" />}
              title="100% natural, unheated"
              body="No dyeing, no glass filling, no heat treatment. What you receive is exactly what you see on this page — verified by lab report." />
            <FeatureCard icon={<Certificate size={22} weight="duotone" className="text-gold" />}
              title="Government-lab certified"
              body="Ships with a government-approved gemological certificate stating natural origin, oval shape and 6.21-carat weight. Included at no extra cost." />
          </div>
        </section>

        {/* Benefits */}
        <section className="mt-16 sm:mt-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
              <Sparkle size={12} weight="fill" /> Astrological benefits
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light">
              What Red Coral is worn for
            </h2>
            <p className="text-muted mt-3 text-sm sm:text-base font-light leading-relaxed">
              Recommended in classical Parashari astrology when Mars is
              debilitated, retrograde, in the 6th/8th/12th, or when the native
              seeks to strengthen a weak Mars. Always confirm with a qualified
              astrologer before wearing.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {BENEFITS.map((b) => (
              <div key={b.title} className="card-surface rounded-md p-5 border border-soft">
                <div className="text-sm font-medium text-cream">{b.title}</div>
                <p className="text-xs text-muted mt-1.5 font-light leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Wearing guide */}
        <section className="mt-16 sm:mt-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
              <HandHeart size={12} weight="fill" /> Wearing guide
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light">
              How to wear this Red Coral correctly
            </h2>
          </div>
          <div className="mt-10 max-w-3xl mx-auto space-y-4">
            {WEARING.map((w, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-gold/10 border border-gold/40 text-gold flex items-center justify-center text-sm font-medium flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <div className="text-sm font-medium text-cream">{w.title}</div>
                  <p className="text-xs sm:text-sm text-muted mt-1 font-light leading-relaxed">{w.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reviews */}
        <section className="mt-16 sm:mt-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
              <Star size={12} weight="fill" /> Customer reviews
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light">
              4.9 / 5 · 51 verified reviews
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {REVIEWS.map((r, i) => (
              <div key={i} className="card-surface rounded-md p-5 border border-soft">
                <div className="flex items-center gap-0.5 text-gold mb-2">
                  {Array.from({ length: r.rating }).map((_, k) => <Star key={k} size={13} weight="fill" />)}
                </div>
                <p className="text-xs sm:text-sm text-cream/90 font-light leading-relaxed">"{r.body}"</p>
                <div className="text-xs text-muted mt-3">— {r.name} · {r.loc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-16 sm:mt-20 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
              <Sparkle size={12} weight="fill" /> FAQ
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light">
              Common questions before you buy
            </h2>
          </div>
          <div className="mt-8 space-y-3" data-testid="product-faq">
            {FAQS.map((f, i) => <FaqRow key={i} q={f.q} a={f.a} />)}
          </div>
        </section>

        {/* Final CTA */}
        <div className="mt-16 sm:mt-20 card-surface rounded-2xl border border-gold/40 p-6 sm:p-10 text-center">
          <h3 className="font-serif-display text-2xl sm:text-3xl font-light">
            Ready to take home this <span className="text-gold">Italian Red Coral</span>?
          </h3>
          <p className="text-muted text-sm mt-2 font-light">
            One-of-a-kind piece · Government-lab certificate included · Ships insured in 3–5 days.
          </p>
          <button
            onClick={() => setCheckoutOpen(true)}
            className="btn-primary rounded-full px-8 py-3 mt-5 text-sm sm:text-base font-medium inline-flex items-center gap-2"
            data-testid="product-buy-btn-bottom"
          >
            <Sparkle size={16} weight="fill" />
            Buy Now — ₹{PRODUCT.price_inr.toLocaleString("en-IN")}
          </button>
          <div className="mt-4 text-xs text-muted">
            Questions?{" "}
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-gold underline decoration-dotted">
              DM @messengerofshiva
            </a>{" "}or call{" "}
            <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-gold underline decoration-dotted">
              {SUPPORT_PHONE_DISPLAY}
            </a>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/store" className="text-xs text-muted hover:text-gold inline-flex items-center gap-1.5">
            <ArrowLeft size={12} /> Back to Store
          </Link>
        </div>
      </div>

      {/* Checkout drawer */}
      {checkoutOpen && (
        <CheckoutModal
          product={PRODUCT}
          onClose={() => setCheckoutOpen(false)}
          onSuccess={(payload) => { setCheckoutOpen(false); setConfirmedOrder(payload); }}
        />
      )}

      {/* Order confirmed modal */}
      {confirmedOrder && (
        <OrderConfirmedModal
          data={confirmedOrder}
          onClose={() => setConfirmedOrder(null)}
        />
      )}
    </PageShell>
  );
}

/* ─── Sub-components ─────────────────────────────────────────── */

function ProductMedia() {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-soft bg-gradient-to-br from-[#3a0f1a] via-[#1a0f2e] to-[#2a1a55]"
         style={{ aspectRatio: "1 / 1" }}
         data-testid="product-media">
      <video
        src={CORAL_VIDEO_URL}
        autoPlay loop muted playsInline controls preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
        aria-label="6.21 carat Italian Red Coral, rotating live view"
      >
        Your browser doesn't support inline video. Please tap the play icon.
      </video>
      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] label-eyebrow bg-bg/80 border border-soft rounded-full px-2.5 py-1 pointer-events-none">
        <Sparkle size={10} weight="fill" /> Live 360° view
      </div>
      <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[10px] font-semibold bg-emerald-500 text-white rounded-full px-2.5 py-1 shadow-lg pointer-events-none">
        <CheckCircle size={11} weight="fill" /> In stock · 1 piece only
      </div>
    </div>
  );
}

function AttrRow({ k, v }) {
  return (
    <div className="border-b border-soft/50 pb-2">
      <div className="text-[10px] uppercase tracking-widest text-muted">{k}</div>
      <div className="text-sm text-cream mt-0.5">{v}</div>
    </div>
  );
}

function PromiseBadge({ icon, title, body }) {
  return (
    <div className="card-surface rounded-md p-2.5 text-center border border-soft">
      <div className="flex justify-center text-gold">{icon}</div>
      <div className="text-[11px] font-medium text-cream mt-1">{title}</div>
      <div className="text-[10px] text-muted mt-0.5 leading-tight">{body}</div>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="card-surface rounded-md p-5 border border-soft">
      <div>{icon}</div>
      <div className="text-sm font-medium mt-3">{title}</div>
      <p className="text-xs text-muted mt-1.5 font-light leading-relaxed">{body}</p>
    </div>
  );
}

function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`card-surface rounded-lg border transition-colors ${open ? "border-gold/40" : "border-soft"}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full text-left px-5 py-4 flex items-start justify-between gap-4"
              data-testid="product-faq-q" aria-expanded={open}>
        <span className="text-sm font-medium text-cream leading-snug">{q}</span>
        <CaretDown size={14} className={`text-gold flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 -mt-1 text-sm text-muted font-light leading-relaxed">{a}</div>}
    </div>
  );
}

const INPUT_CLS = "w-full px-4 py-2.5 rounded-lg border border-soft focus:border-amber-400 focus:outline-none text-sm bg-transparent";

/* ─── Checkout ───────────────────────────────────────────────── */

function CheckoutModal({ product, onClose, onSuccess }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const bind = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  useEffect(() => { loadRazorpay(); }, []);

  const handlePay = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    // Client-side sanity
    if (!form.customer_name.trim())       return toast.error("Please enter your name.");
    if (!form.customer_email.trim())      return toast.error("Please enter your email.");
    if ((form.customer_phone.replace(/\D/g, "") || "").length < 10)
                                          return toast.error("Please enter a valid phone number.");
    if (!form.address_line1.trim())       return toast.error("Please enter your address.");
    if (!form.city.trim())                return toast.error("Please enter your city.");
    if (!form.state.trim())               return toast.error("Please enter your state.");
    if ((form.pincode.replace(/\D/g, "") || "").length < 4)
                                          return toast.error("Please enter a valid PIN / ZIP code.");

    setSubmitting(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Razorpay failed to load. Please retry.");
      const { data } = await api.post("/store/order/create", {
        sku: product.sku,
        ...form,
      });
      const rzp = createRazorpayCheckout({
        key: data.key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: "AstroKuberChat Store",
        description: data.product.name,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: "#D4AF5F" },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
        handler: async (resp) => {
          try {
            const v = await api.post("/store/order/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            onSuccess({
              order_id: v.data.order_id,
              customer_name: v.data.customer_name || form.customer_name,
              product_name: v.data.product?.name,
              amount_inr: v.data.amount_inr,
            });
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment could not be verified. Please contact support.");
          } finally {
            setSubmitting(false);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Could not start payment. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="checkout-modal">
      <div className="card-surface rounded-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto p-6 sm:p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted hover:text-gold" data-testid="checkout-close-btn">
          <X size={18} />
        </button>
        <div className="text-gold label-eyebrow inline-flex items-center gap-2">
          <Sparkle size={12} weight="fill" /> Checkout · Secure via Razorpay
        </div>
        <h3 className="font-serif-display text-2xl mt-2 font-light">
          Ship your Red Coral to
        </h3>
        <div className="mt-1 text-xs text-muted">
          {product.name} · <b className="text-cream">₹{product.price_inr.toLocaleString("en-IN")}</b> · one-time · GST-inclusive
        </div>

        <form onSubmit={handlePay} className="mt-5 space-y-3">
          <Field label="Full Name" required>
            <input required maxLength={60} className={INPUT_CLS} data-testid="chk-name" {...bind("customer_name")} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" required>
              <input required type="email" maxLength={100} className={INPUT_CLS} data-testid="chk-email" {...bind("customer_email")} />
            </Field>
            <Field label="Phone (with country code)" required>
              <input required inputMode="tel" maxLength={20} placeholder="+91 ..." className={INPUT_CLS} data-testid="chk-phone" {...bind("customer_phone")} />
            </Field>
          </div>
          <Field label="Address (Line 1)" required>
            <input required maxLength={120} className={INPUT_CLS} data-testid="chk-addr1" {...bind("address_line1")} />
          </Field>
          <Field label="Address (Line 2)">
            <input maxLength={120} className={INPUT_CLS} data-testid="chk-addr2" {...bind("address_line2")} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="City" required>
              <input required maxLength={40} className={INPUT_CLS} data-testid="chk-city" {...bind("city")} />
            </Field>
            <Field label="State" required>
              <input required maxLength={40} className={INPUT_CLS} data-testid="chk-state" {...bind("state")} />
            </Field>
            <Field label="PIN / ZIP" required>
              <input required inputMode="numeric" maxLength={10} className={INPUT_CLS} data-testid="chk-pin" {...bind("pincode")} />
            </Field>
          </div>
          <Field label="Order note (optional)">
            <textarea maxLength={300} rows={2} className={`${INPUT_CLS} resize-none`} data-testid="chk-notes" {...bind("notes")} />
          </Field>

          <button type="submit" disabled={submitting}
                  className="btn-primary rounded-full w-full py-3 text-sm font-medium mt-2 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="chk-pay-btn">
            {submitting ? (
              <>Processing…</>
            ) : (
              <><Lock size={14} weight="fill" /> Pay ₹{product.price_inr.toLocaleString("en-IN")} securely</>
            )}
          </button>
          <div className="text-[10px] text-muted text-center">
            <Lock size={10} className="inline mr-1" />
            Payment powered by <b className="text-cream/80">Razorpay</b> · UPI, Cards, Netbanking, Wallets
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-muted">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function OrderConfirmedModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="order-confirmed-modal">
      <div className="card-surface rounded-2xl max-w-md w-full p-7 relative text-center border border-gold/40">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted hover:text-gold" data-testid="oc-close-btn">
          <X size={18} />
        </button>
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/60 mx-auto flex items-center justify-center">
          <CheckCircle size={36} weight="fill" className="text-emerald-400" />
        </div>
        <h3 className="font-serif-display text-2xl sm:text-3xl mt-4 font-light">
          Order confirmed
          {data.customer_name ? <>,<br/><span className="text-gold">{firstName(data.customer_name)}!</span></> : "!"}
        </h3>
        <p className="text-cream text-sm mt-3 font-light leading-relaxed">
          Thank you for buying{" "}
          <b className="text-cream">{data.product_name || "your gemstone"}</b> from AstroKuberChat Store.
        </p>
        <p className="text-muted text-sm mt-3 font-light leading-relaxed">
          Your order is confirmed and we will reach out to you shortly (within a few minutes)
          to verify shipping details and schedule dispatch.
        </p>
        <div className="mt-5 rounded-md border border-gold/30 bg-[#FFF6E0] px-4 py-3 text-left">
          <div className="text-[10px] uppercase tracking-widest text-[#7C2D12]">For any queries</div>
          <div className="mt-1 text-sm text-[#3F2D1B] font-medium">
            <Phone size={14} weight="fill" className="inline mr-1.5" />
            <a href={`tel:${SUPPORT_PHONE_TEL}`} className="hover:underline">{SUPPORT_PHONE_DISPLAY}</a>
          </div>
          <div className="text-xs text-[#7C2D12] mt-1">
            Or DM{" "}
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">
              @messengerofshiva
            </a>{" "}on Instagram.
          </div>
        </div>
        <div className="mt-4 text-[11px] text-muted">
          Reference · <span className="font-mono">{data.order_id}</span>
        </div>
        <button onClick={onClose} className="btn-primary rounded-full px-6 py-2.5 mt-6 text-sm font-medium"
                data-testid="oc-done-btn">
          Done
        </button>
      </div>
    </div>
  );
}

function firstName(s) {
  return String(s || "").trim().split(/\s+/)[0] || "";
}
